'use client';

import { ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReportChart } from '@/app/reports/components/ReportChart';
import { ReportDataTable } from '@/app/reports/components/ReportDataTable';
import { ReportInsightsPanel } from '@/app/reports/components/ReportInsightsPanel';
import { computeReportInsights } from '@/app/reports/components/report-insights';
import { isTimeSeriesSeries } from '@/app/reports/components/report-chart-utils';
import { useReportLookupMaps } from '@/app/reports/hooks/useReportLookupMaps';
import { enrichSeriesPoints, enrichTableRows } from '@/app/reports/utils/enrich-report-display';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';

type VisualizationSpec = {
  id: string;
  role: string;
  kind: string;
  titleKey: string;
};

type VisualizationPlan = {
  analysisFocus: string;
  primaryKind: string;
  visualizations: VisualizationSpec[];
};

type ReportData = {
  series: { date: string; value: number; label?: string }[];
  table?: Record<string, string | number | null>[];
  tableColumns?: {
    key: string;
    labelKey: string;
    format?: 'text' | 'number' | 'money' | 'percent' | 'date';
  }[];
  currency?: string;
};

type ReportVisualizationViewProps = {
  plan: VisualizationPlan;
  data: ReportData;
  isPercentage?: boolean;
};

const HIDDEN_KINDS = new Set(['metric', 'sparkline', 'table', 'gauge']);

const isChartSpec = (spec: VisualizationSpec) => !HIDDEN_KINDS.has(spec.kind);

const buildRawRows = (data: ReportData) =>
  data.table ??
  data.series.map((point) => ({
    period: point.label ?? point.date,
    value: point.value,
  }));

const rawColumns = (isTimeSeries: boolean) =>
  isTimeSeries
    ? [
        { key: 'period', labelKey: 'insights.rawColumns.period', format: 'date' as const },
        { key: 'value', labelKey: 'insights.rawColumns.value', format: 'number' as const },
      ]
    : [
        { key: 'period', labelKey: 'insights.rawColumns.segment', format: 'text' as const },
        { key: 'value', labelKey: 'insights.rawColumns.value', format: 'number' as const },
      ];

export function ReportVisualizationView({ plan, data, isPercentage }: ReportVisualizationViewProps) {
  const { t } = useTranslation('reports');
  const lookupMaps = useReportLookupMaps();
  const [rawOpen, setRawOpen] = useState(false);
  const displayData = useMemo(
    () => ({
      ...data,
      series: enrichSeriesPoints(data.series, lookupMaps),
      table: data.table ? enrichTableRows(data.table, lookupMaps) : data.table,
    }),
    [data, lookupMaps],
  );
  const isTimeSeries = isTimeSeriesSeries(displayData.series);
  const chartSpecs = plan.visualizations.filter(isChartSpec);
  const insights = useMemo(
    () =>
      computeReportInsights({
        focus: plan.analysisFocus,
        series: displayData.series,
        currency: displayData.currency,
        isPercentage,
      }),
    [plan.analysisFocus, displayData.series, displayData.currency, isPercentage],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('analysisFocus.label')}
        </span>
        <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
          {t(`analysisFocus.${plan.analysisFocus}`)}
        </span>
      </div>

      <ReportInsightsPanel insights={insights} />

      <div className={`grid gap-4 ${chartSpecs.length > 1 ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
        {chartSpecs.map((spec, index) => {
          const title = t(spec.titleKey, { defaultValue: spec.id });
          const isPrimary = index === 0;
          return (
            <section
              key={spec.id}
              className={`rounded-xl border border-border/80 bg-card p-4 shadow-sm sm:p-5 ${
                isPrimary && chartSpecs.length > 1 ? 'lg:col-span-2' : ''
              }`}
            >
              <h3 className="text-sm font-semibold text-foreground">{title}</h3>
              <p className="mt-1 text-xs text-muted-foreground">{t('viewer.chartHint')}</p>
              <div className="mt-3">
                <ReportChart
                kind={spec.kind as Parameters<typeof ReportChart>[0]['kind']}
                series={displayData.series}
                currency={data.currency}
                isPercentage={isPercentage}
              />
              </div>
            </section>
          );
        })}
      </div>

      <Collapsible open={rawOpen} onOpenChange={setRawOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground">
            <ChevronDown className={`h-4 w-4 transition-transform ${rawOpen ? 'rotate-180' : ''}`} />
            {t('insights.rawDataToggle', { count: displayData.series.length })}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3">
          <ReportDataTable
            rows={buildRawRows(displayData)}
            columns={displayData.tableColumns ?? rawColumns(isTimeSeries)}
            currency={displayData.currency}
            lookupMaps={lookupMaps}
          />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
